import { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ActivityIndicator,
  TextInput,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { ChevronLeft, User } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import OnboardingProgress from '@/components/OnboardingProgress';

export default function OnboardingHandle() {
  const router = useRouter();
  const { user, profile, refreshProfile } = useAuth();
  const { theme } = useTheme();
  const c = theme.colors;
  const insets = useSafeAreaInsets();

  const [handle, setHandle] = useState(profile?.handle ?? '');
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const validateHandle = (value: string): string | null => {
    if (value.length < 3) return 'Handle must be at least 3 characters';
    if (value.length > 30) return 'Handle must be 30 characters or less';
    if (!/^[a-zA-Z0-9_]+$/.test(value)) return 'Only letters, numbers, and underscores allowed';
    return null;
  };

  const checkHandleAvailability = async (value: string): Promise<boolean> => {
    // If user already owns this handle, it's available by definition
    if (profile?.handle === value) return true;

    try {
      const query = supabase
        .from('profiles')
        .select('handle')
        .eq('handle', value);

      // Exclude current user's own profile
      if (profile?.id) {
        query.neq('id', profile.id);
      }

      const { data, error } = await query.maybeSingle();
      
      if (error) throw error;
      return !data; // Available if no data found
    } catch {
      throw new Error('Error checking handle availability');
    }
  };

  const handleContinue = async () => {
    const trimmed = handle.trim();
    
    // Validate format
    const validation = validateHandle(trimmed);
    if (validation) {
      setError(validation);
      return;
    }

    setChecking(true);
    setError('');

    try {
      // Check availability
      const isAvailable = await checkHandleAvailability(trimmed);
      if (!isAvailable) {
        setError('Handle is already taken');
        setChecking(false);
        return;
      }

      // Save to profile
      setSaving(true);
      if (user?.id) {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ handle: trimmed, display_name: trimmed })
          .eq('id', user.id);

        if (updateError) {
          if (updateError.message?.includes('only be set once')) {
            // Handle already set — just continue
            await refreshProfile?.();
            router.replace('/(onboarding)/choose-path');
            return;
          }
          setError(updateError.message || 'Failed to save handle. Please try again.');
          setSaving(false);
          setChecking(false);
          return;
        }

        await refreshProfile?.();
      }

      // Navigate to next step
      router.replace('/(onboarding)/choose-path');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }

    setChecking(false);
    setSaving(false);
  };

  return (
    <View style={[styles.container, { backgroundColor: c.background }]}>
      <TouchableOpacity style={[styles.backBtn, { top: insets.top + 8 }]} onPress={() => router.back()}>
        <ChevronLeft size={24} color={c.text} />
      </TouchableOpacity>

      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        <View style={styles.inner}>
          <OnboardingProgress step={2} />
          <User size={36} color={c.accent} />
          <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>
            PICK YOUR HANDLE
          </Text>
          <Text style={[styles.subtitle, { color: c.textSecondary }]}>
            This is how rivals will know you.
          </Text>

          <View style={styles.inputSection}>
            <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>HANDLE</Text>
            <TextInput
              style={[
                styles.input,
                { 
                  color: c.text, 
                  backgroundColor: c.surface,
                  borderColor: error ? (c.error || '#ff4444') : c.border 
                }
              ]}
              value={handle}
              onChangeText={(text) => {
                setHandle(text.toLowerCase().replace(/[^a-z0-9_]/g, ''));
                setError('');
              }}
              placeholder="ironlifter"
              placeholderTextColor={c.textTertiary}
              autoCapitalize="none"
              autoCorrect={false}
              returnKeyType="go"
              onSubmitEditing={handleContinue}
              maxLength={30}
            />
            {error ? (
              <Text style={[styles.errorText, { color: c.error || '#ff4444' }]}>{error}</Text>
            ) : (
              <Text style={[styles.hint, { color: c.textTertiary }]}>
                Lowercase only · 3-30 characters · letters, numbers, and underscores
              </Text>
            )}
          </View>
        </View>

        <View style={styles.bottom}>
          <Pressable
            style={[
              styles.primaryBtn, 
              { 
                backgroundColor: c.accent, 
                opacity: (!handle.trim() || checking || saving) ? 0.6 : 1 
              }
            ]}
            onPress={handleContinue}
            disabled={!handle.trim() || checking || saving}
            android_ripple={{ color: 'rgba(0,0,0,0.15)', borderless: false }}
            hitSlop={{ top: 8, bottom: 8 }}
            accessibilityRole="button"
          >
            {(checking || saving) ? (
              <ActivityIndicator size="small" color={c.accentForeground} />
            ) : (
              <Text style={[styles.primaryBtnText, { color: c.accentForeground, fontFamily: TYPO.heading.bold }]}>
                CONTINUE
              </Text>
            )}
          </Pressable>
        </View>
      </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  backBtn: { position: 'absolute', top: 50, left: 16, zIndex: 10, padding: 8 },
  scrollContent: { flexGrow: 1 },
  inner: { flex: 1, paddingHorizontal: 24, paddingTop: 80, alignItems: 'center', gap: 16 },
  title: { fontSize: 28, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 1 },
  subtitle: { fontSize: 15, textAlign: 'center', lineHeight: 22, maxWidth: 340, marginBottom: 8 },
  inputSection: { width: '100%', gap: 6 },
  sectionLabel: { fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase' },
  input: { 
    borderWidth: 1, 
    borderRadius: 2, 
    paddingHorizontal: 14, 
    height: 52, 
    fontSize: 18, 
    fontWeight: '700' 
  },
  hint: { fontSize: 12 },
  errorText: { fontSize: 13 },
  bottom: { paddingHorizontal: 24, paddingBottom: 40, alignItems: 'center' },
  primaryBtn: { width: '100%', paddingVertical: 18, borderRadius: 0, alignItems: 'center' },
  primaryBtnText: { fontSize: 18, textTransform: 'uppercase', letterSpacing: 1 },
});
